'use client';

import { type DependencyList, useEffect, useRef } from 'react';

import { Chart, type ChartConfiguration, type ChartType } from 'chart.js';

import { registerCharts } from './chart-registry';

registerCharts();

/**
 * Owns a chart.js instance for the canvas it hands back: built on mount, then fed the new
 * configuration whenever `deps` change instead of being torn down and rebuilt, so the
 * chart animates from the values it is showing to the new ones rather than replaying from
 * zero. `configure` runs inside the effect, so it must close over everything in `deps`.
 *
 * Plugins are read once, at creation: a plugin that draws changing values has to read them
 * through a ref rather than capture them.
 */
export function useChart<T extends ChartType>(
  configure: () => ChartConfiguration<T>,
  deps: DependencyList,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart<T> | null>(null);

  useEffect(() => {
    const context = canvasRef.current?.getContext('2d');

    if (!context) {
      return;
    }

    const configuration = configure();
    const chart = chartRef.current;

    if (chart) {
      chart.data = configuration.data;

      if (configuration.options) {
        chart.options = configuration.options;
      }

      chart.update();
      return;
    }

    chartRef.current = new Chart(context, configuration);
  }, deps);

  useEffect(
    () => () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    },
    [],
  );

  return canvasRef;
}
